import { RestaurantHours } from "./RestaurantHours";
import { Category } from "../menu/Category";
import { Product } from "../menu/Product";
import { RestaurantInformation } from "../session/RestaurantInformation";
import { PaymentOptions } from "./RestaurantPaymentOptions";
import { Status } from "./RestaurantStatus";
import { CurrentDateTimeInfo } from "../menu/CurrentDateTimeInfo";
import { AboutRestaurant } from "./AboutRestaurant";
import { RestaurantOffers } from "./RestaurantOffers";
import { RestaurantReceiptRules } from "./RestaurantReceiptRules";

/**
 * Defines the "avanto-restaurants" table on DynamoDB.
 * Has the business information of the restaurant, but NOT it's menu...
 */
export class Restaurant {

	private static readonly RESTAURANT_IMG_PLACEHOLDER_PATH 		= "https://avanto-eats-files.s3.amazonaws.com/images/placeholders/restaurantLogoPlaceholder.png"
	private static readonly RESTAURANT_BANNER_PLACEHOLDER_PATH 		= "https://avanto-eats-files.s3.amazonaws.com/images/placeholders/restaurantBannerPlaceholder.jpg";
	private static readonly RESTAURANT_FOOD_IMG_PLACEHOLDER_PATH 	= "https://avanto-eats-files.s3.amazonaws.com/images/placeholders/restaurantFoodImgPlaceholder.png";

	region: string;
	clientId: string;
	title: string;
	subtitle: string;
	logo: string;
	bannerImg:string;
	foodImg:string;
	address: string;
	province: string;
	zip: string;
	coordinates: Coordinates;
	phone: string;
	status: Status;
	restaurantMsg:string;
	paymentOptions:PaymentOptions;
	tags: string[];
	hiddenTags: string[];
	priceRating: Number;
	hasPickup: boolean;
    hasDelivery: boolean;
	pickupHours: RestaurantHours;
	deliveryHours: RestaurantHours;
	categories:Category[];
	restaurantModule:string | null;
	sendToEmails:string[];
	sendToSMS:string[];
	tabletInfo:{
		tabletPassword:string,
		token:string
	}[];
	minimumOrderTotalPickup:number;
	minimumOrderTotalDelivery:number;
	aboutRestaurant:AboutRestaurant|null;
	specialOffers:RestaurantOffers[];
	rewardPointOffers:RestaurantOffers[];
	minTimeForPickup:number | null;
	minTimeForDelivery:number | null;
	restaurantSquareLocationId:string|null;
	receiptRules:RestaurantReceiptRules|null;
	googleReviewLink:string|null;
	
	/**
	 * Constructor
	 * @param region PARTITION KEY: Region where the restaurant is located (ex: Oshawa)
	 * @param clientId SORT KEY: Unique identifier of the restaurant, should be the restaurant name, separated by dashes (ex: burger-king) 
	 * @param title Name of the restaurant, as displayed to the user (ex: Burger King)
	 * @param subtitle Some additional text you want to put below the title (ex: 10% off for orders above 10$)
	 * @param logo Path to the restaurant logo. The logo should be uploaded on Amazon S3 (ex: https://s3.us-east-2.amazonaws.com/avanto-eats-client-imgs/whisky-johns-bar-and-grill/logo.png)
	 * @param bannerImg Banner image shown on the restaurant menu page
	 * @param foodImg Image showing a the type of food they serve at the restaurant
	 * @param address (ex: 55 ironside crescent)
	 * @param province (ex: Ontario)
	 * @param zip (ex: M1T 2Z1)
	 * @param coordinates Coordinates of the restaurant. Check the Coordinates object for more information
	 * @param phone Phone number of the restaurant (ex: (647) 123-4567 )
	 * @param status Contains some status information about the restaurant. Check the Status object for more information
	 * @param restaurantMsg special restaurant message, shown to the customers
	 * @param paymentOptions Contains the payment options for this restaurant
	 * @param tags Tags given to the restaurant describing the type of service it provides. Will be seen by the users and can be used to search restaurants (ex: burgers, pizza, chinese, greek...)
	 * @param hiddenTags Same thing as the tags, but invisible to the user. Only used when searching for the restaurant
	 * @param priceRatingNumber from 1 to 5 defining how expensive the restaurant is (ex: if 1, the restaurant will get a "$"" rating. If 5, the restaurant will get "$$$$$" rating). If you have no clue what Im talking about google any restaurant... if 0, nothing will be shown.
	 * @param hasPickup Whether the restaurant has pickup or not
	 * @param hasDelivery Whether the restaurant has delivery or not
	 * @param pickupHours What time the restaurant opens and what time it closes FOR PICKUP
	 * @param deliveryHours What time the restaurant opens and what time it closes FOR PICKUP
	 * @param categories List of categories related to this restaurant
	 * @param minimumOrderTotalPickup Minimum total your order must have in order to be processed for pickup
	 * @param minimumOrderTotalDelivery Minimum total your order must have in order to be processed for delivery
	 * @param aboutRestaurant special information related to the restaurant. AvantoEats may not be using this, but some standalone apps do.
	 * @param specialOffers special offers related to this restaurant
	 * @param rewardPointOffers reward point related to this restaurant
	 * @param minTimeForPickup time the customer has to wait until his pickup order is ready
	 * @param minTimeForDelivery time the customer has to wait until his delivery order is ready 
	 * @param restaurantSquareLocationId We have different "locations" on our SQUARE payments account. If the restaurant has it's own location, we can set it's ID here. If not, we use the default location.
	 * @param receiptRules special rules for the receipt of this restaurant
	 * @param googleReviewLink if the restaurant has this, there will be an option for a review at the end of the checkout process
	 */
	constructor(region: string, clientId: string, title: string, subtitle: string | null, logo: string, bannerImg:string, foodImg:string, address: string | null, province: string | null, zip: string | null, 
				phone: string | null, status: Status, restaurantMsg:string | null, paymentOptions:PaymentOptions, tags: string[], hiddenTags:string[], coordinates: Coordinates,priceRating:Number | null, hasPickup:boolean | null, hasDelivery:boolean | null,
				pickupHours: RestaurantHours, deliveryHours:RestaurantHours, categories:Category[], restaurantModule:string | null, sendToEmails:string[],
				sendToSMS:string[], tabletInfo:{ tabletPassword:string, token:string }[],minimumOrderTotalPickup:number, minimumOrderTotalDelivery:number, 
				aboutRestaurant:AboutRestaurant|null, specialOffers:RestaurantOffers[], rewardPointOffers:RestaurantOffers[], minTimeForPickup:number | null,
				 minTimeForDelivery:number | null, restaurantSquareLocationId:string|null, receiptRules:RestaurantReceiptRules|null, googleReviewLink:string|null) {

		//Mandatory fields
		this.region = region;
		this.clientId = clientId;
		this.title = title;

		//Optional fields
		this.subtitle 		 = (subtitle==null)?"":subtitle;
		this.logo 			 = (logo==null)?Restaurant.RESTAURANT_IMG_PLACEHOLDER_PATH:logo;
		this.bannerImg		 = (bannerImg==null)?Restaurant.RESTAURANT_BANNER_PLACEHOLDER_PATH:bannerImg;
		this.foodImg	     = (foodImg==null)?Restaurant.RESTAURANT_FOOD_IMG_PLACEHOLDER_PATH:foodImg;
		this.address 		 = (address==null)?"":address;
		this.province 		 = (province==null)?"":province;
		this.zip 			 = (zip==null)?"":zip;
		this.phone 			 = (phone==null)?"":phone;
		this.status 		 = (status==null)?Status.generateDefaultStatus():status;
		this.restaurantMsg   = (restaurantMsg == null)?"":restaurantMsg;
		this.paymentOptions = (paymentOptions==null)?PaymentOptions.generateDefaultPaymentOptions():paymentOptions;
		this.tags 			 = (tags != null) ? tags : [];
		this.hiddenTags 	 = (hiddenTags != null) ? hiddenTags : [];
		this.coordinates 	 = (coordinates==null)?Coordinates.generateDefaultCoordinates():coordinates;
		this.priceRating 	 = (priceRating==null)?0:priceRating;
		this.hasPickup		 = (hasPickup==null)?true:hasPickup;
		this.hasDelivery	 = (hasDelivery==null)?true:hasDelivery;
		this.pickupHours 	 = (pickupHours==null)?RestaurantHours.generateDefaultRestaurantHours():pickupHours;
		this.deliveryHours 	 = (deliveryHours==null)?RestaurantHours.generateDefaultRestaurantHours():deliveryHours;
		this.categories 	 = categories;
		this.restaurantModule   = restaurantModule;
		this.sendToEmails 			   = (sendToEmails == null)?[]:sendToEmails;
		this.sendToSMS 				   = (sendToSMS == null)?[]:sendToSMS;
		this.tabletInfo			       = (tabletInfo == null)?[]:tabletInfo;
		this.minimumOrderTotalPickup   = (minimumOrderTotalPickup==null)?0:minimumOrderTotalPickup;
		this.minimumOrderTotalDelivery = (minimumOrderTotalDelivery==null)?0:minimumOrderTotalDelivery;

		this.aboutRestaurant	= aboutRestaurant
		this.specialOffers		= (specialOffers==null)?[]:specialOffers;
		this.rewardPointOffers  = (rewardPointOffers==null)?[]:rewardPointOffers;

		this.minTimeForPickup 	 = minTimeForPickup;
		this.minTimeForDelivery  = minTimeForDelivery;
		this.restaurantSquareLocationId = restaurantSquareLocationId;

		this.receiptRules = receiptRules==null?RestaurantReceiptRules.generateDefaultReceiptRules():receiptRules;

		this.googleReviewLink = googleReviewLink;
	}

	/** A restaurant's unique identifier is a string containing it's primary key and sort key. 
	 * It will be on the format : "region.clientId" 
	 * This is used by all the menu items of this restaurant as their primary key*/
	getUniqueIdentifier():string{
		return this.region+"."+this.clientId;
	}

	isAddressValid(){
		return this.address!=null && this.address!="" && this.province!=null && this.province!="" && this.zip!=null && this.zip!="";
	}

	/** Converts this object into a format that can be saved on our database (DynamoDB) */
	toAWSObject(){
		//TODO: :-D
		// //Mandatory attributes
		// let restaurantObj = {
		// 	"region" : this.region,
		// 	"clientId" : this.clientId,
		// 	"title" : this.title
		// } as any;

		// //Optional attributes
		// if(this.subtitle!=null && this.subtitle!="")	{ 
		// 	restaurantObj["subtitle"] = this.subtitle 
		// }

		// if(this.logo!=null && this.logo!="")		{ 
		// 	restaurantObj["logo"] = this.logo 
		// }

		// if(this.address!=null && this.address!="")	{ 
		// 	restaurantObj["address"] = this.address 
		// }

		// if(this.province!=null && this.province!="")	{ 
		// 	restaurantObj["province"] = this.province 
		// }

		// if(this.zip!=null && this.zip!="")		{ 
		// 	restaurantObj["zip"] = this.zip 
		// }

		// if(this.phone!=null && this.phone!="")	{ 
		// 	restaurantObj["phone"] = this.phone 
		// }

		// if(this.status!=null)	{ 
		// 	restaurantObj["status"] = this.status.toAWSObject() 
		// }

		// if(this.tags!=null && this.tags.length>0) { 
		// 	restaurantObj["tags"] = this.tags 
		// }

		// if(this.hiddenTags!=null && this.hiddenTags.length>0) { 
		// 	restaurantObj["hiddenTags"] = this.hiddenTags 
		// }

		// if(this.coordinates!=null)	{ 
		// 	restaurantObj["coordinates"] = this.coordinates.toAWSObject(); 
		// }
		// if(this.priceRating!=null  && this.priceRating>0){
		// 	restaurantObj["priceRating"] = this.priceRating;
		// }
		// if(this.pickupHours!=null){
		// 	restaurantObj["pickupHours"] = this.pickupHours.toAWSObject();
		// }
		// if(this.deliveryHours!=null){
		// 	restaurantObj["deliveryHours"] = this.deliveryHours.toAWSObject();
		// }

		// return restaurantObj;
	}

	/**
	 * Creates a new restaurant object from an AWS object obtained from DynamoDB
	 * TODO:(SAY HERE WHICH LAMBDA SERVICE IS BEING USED)
	 * @param restaurantAwsItem 
	 * @param categoriesAwsData 
	 */
	static newFromAWS(restaurantAwsItem:any,categoriesAwsData?:any, serverDateTime?:CurrentDateTimeInfo){
		let restaurantCategories:Category[] = [];
		if(categoriesAwsData!=null){		
			restaurantCategories = Restaurant.getRestaurantCategoriesFromAws(categoriesAwsData,serverDateTime);
		}

		let specialOffers:RestaurantOffers[] = [];
		if(restaurantAwsItem.specialOffers!=null){	
			restaurantAwsItem.specialOffers.forEach((offer:any) => {
				specialOffers.push(RestaurantOffers.newFromAws(offer));
			});	
		}

		let rewardPointOffers:RestaurantOffers[] = [];
		if(restaurantAwsItem.rewardPointOffers!=null){	
			restaurantAwsItem.rewardPointOffers.forEach((offer:any) => {
				rewardPointOffers.push(RestaurantOffers.newFromAws(offer));
			});	
		}
		
		const newRestaurant = new Restaurant(
			restaurantAwsItem.region, 
			restaurantAwsItem.clientId, 
			restaurantAwsItem.title, 
			restaurantAwsItem.subtitle,
			restaurantAwsItem.logo, 
			restaurantAwsItem.bannerImg,
			restaurantAwsItem.foodImg, 
			restaurantAwsItem.address, 
			restaurantAwsItem.province, 
			restaurantAwsItem.zip, 
			restaurantAwsItem.phone,
			Status.newFromAws(restaurantAwsItem.status), 
			restaurantAwsItem.restaurantMsg,
			PaymentOptions.newFromAws(restaurantAwsItem.paymentOptions), 
			restaurantAwsItem.tags, restaurantAwsItem.hiddenTags,
			Coordinates.newFromAws(restaurantAwsItem.coordinates), 
			restaurantAwsItem.priceRating, restaurantAwsItem.hasPickup,
			restaurantAwsItem.hasDelivery,
			RestaurantHours.newFromAws(restaurantAwsItem.pickupHours), 
			RestaurantHours.newFromAws(restaurantAwsItem.deliveryHours), 
			restaurantCategories, restaurantAwsItem.restaurantModule,
			restaurantAwsItem.sendToEmails,restaurantAwsItem.sendToSMS,
			restaurantAwsItem.tabletInfo, 
			restaurantAwsItem.minimumOrderTotalPickup, 
			restaurantAwsItem.minimumOrderTotalDelivery,
			AboutRestaurant.newFromAws(restaurantAwsItem.aboutRestaurant),
			specialOffers, 
			rewardPointOffers,
			restaurantAwsItem.minTimeForPickup,
			restaurantAwsItem.minTimeForDelivery,
			restaurantAwsItem.restaurantSquareLocationId,
			RestaurantReceiptRules.newFromAws(restaurantAwsItem.receiptRules),
			restaurantAwsItem.googleReviewLink
		);
		return newRestaurant;
	}

	static getRestaurantCategoriesFromAws(data:any, serverDateTime?:CurrentDateTimeInfo):Category[]{
		const restaurantMenu: Category[] = [];

		//The following maps, have their IDs as keys and their objects as values
        const menuCategoriesMap:Map<string,Category> = new Map();
        const menuProductsMap:Map<string,Product> = new Map();

        data.forEach(function (item:any) {
          //If the ID begins with the category prefix, it goes to the menuCategories map
          if(item.id.startsWith(Category.ID_PREFIX)){
            const newCategory:Category = Category.newFromAWS(item,serverDateTime);
            menuCategoriesMap.set(newCategory.id,newCategory);
          }
          //If the ID begins with the product prefix, it goes to the menuProducts map
          if(item.id.startsWith(Product.ID_PREFIX)){
            const newProduct:Product = Product.newFromAWS(item,serverDateTime);
            menuProductsMap.set(newProduct.id,newProduct);
          }
        });

        //Sets the products inside of their respective categories, provided they are active
        menuProductsMap.forEach(product=>{
          if(product.active){
			  if(menuCategoriesMap.has(product.parentCategoryId)){
				menuCategoriesMap.get(product.parentCategoryId)!.addProduct(product);
			  }else{
				console.error("PRODUCT WITHOUT PARENT CATEGORY DETECTED - "+product.title)
			  }
          }
        });

        menuCategoriesMap.forEach((cat)=>{
          //We won't add inactive categories to the menu...
          if(cat.active){
            cat.sortProducts()
            if(!cat.hasParent()){
              //Categories without a parent (root categories) are added to the menu received by the user
              restaurantMenu.push(cat);
            }else{
              //Categories with a parent (subcategories) are set in their parent's subcategories array
              menuCategoriesMap.get(cat.parentId)!.addSubCategory(cat);
            }
          }
        })
        //Sorts this array based on the display orders of it's categories
		restaurantMenu.sort((a, b) => a.displayOrder < b.displayOrder ? -1 : a.displayOrder > b.displayOrder ? 1 : 0);
		restaurantMenu.forEach(cat=>{
			cat.sortSubCategories();
		})
		
		return restaurantMenu;
	}

	static generateNew(region:string, client:string, title:string){
		return new Restaurant(
			region,
			client,
			title,
			null,
			this.RESTAURANT_IMG_PLACEHOLDER_PATH, 
			this.RESTAURANT_BANNER_PLACEHOLDER_PATH,
			this.RESTAURANT_FOOD_IMG_PLACEHOLDER_PATH,
			null,null,null,null,
			Status.generateDefaultStatus(), null,
			PaymentOptions.generateDefaultPaymentOptions(),
			[],[],Coordinates.generateDefaultCoordinates(),
			null, true, true ,
			RestaurantHours.generateDefaultRestaurantHours(),
			RestaurantHours.generateDefaultRestaurantHours(),
			[],null,[],[],[], 0, 0, null, [], [],null,null,null,null,null)
	}

	static generatePlaceHolder(){
		// return this.generateNew("",)
	}


	generateRestaurantInfoObject():RestaurantInformation{
		return new RestaurantInformation(
			this.region+"."+this.clientId,
			this.title,
			this.zip,
			this.address,
			this.phone,
			this.sendToSMS,
			this.sendToEmails,
			this.hasPickup,
			this.hasDelivery,
			this.rewardPointOffers.length>0,
			this.paymentOptions,
			this.status,
			this.pickupHours,
			this.deliveryHours,
			this.restaurantModule,
			this.minimumOrderTotalPickup,
			this.minimumOrderTotalDelivery,
			this.minTimeForPickup,
			this.minTimeForDelivery,
			this.restaurantSquareLocationId,
			this.receiptRules,
			this.googleReviewLink,
			this.aboutRestaurant,
		)
	}

}
//=================================================================================================================\\

/**
 * Coordinates of the restaurant on the planet.
 */
export class Coordinates {
	public lat: Number;
	public lng: Number;

	/**
	 * Constructor
	 * @param lat Latitude
	 * @param lng Longitude
	 */
	constructor(lat: Number, lng: Number) {
		this.lat = lat;
		this.lng = lng;
	}

	/** 
	 * Determines whether the coordinates given are valid or not.
	 * If I try to create a restaurant with NULL coordinates, I initialize the object with latitude and longitude 0, 
	 * because I need to initialize it with something...
	 */
	isValid(){
		return this.lat!=0 && this.lng!=0;
	}

	/** Returns a string containing the latitude and longitude */
	toString(){
		return "Latitude: "+this.lat+" ; Longitude: "+this.lng
	}

	/** Converts this object into a format that can be saved on our database (DynamoDB) */
	toAWSObject(){
		let coordinateObject = {
			"lat":this.lat,
			"lng":this.lng
		};
		return coordinateObject;
	}

	static generateDefaultCoordinates(){
		return new Coordinates(-1,-1);
	}

	static newFromAws(awsCoordinatesItem:any):Coordinates{
		if(awsCoordinatesItem!=null){
			return new Coordinates(awsCoordinatesItem.lat, awsCoordinatesItem.lng);
		}else{
			return Coordinates.generateDefaultCoordinates();
		}
	}
}